import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AmountUtils, cn, ColorUtils, DateUtils, FormatUtils } from '@shared/utils';
import type { TransferRequestResponse } from '@shared/api';
import { Table, TableCellType } from '@shared/widgets';

interface ITableProps {
  isLoading?: boolean;
  error?: string | null;
  onRefresh?: () => void;
  data: TransferRequestResponse[];
  currentPage: number;
  totalItemCount: number;
  countItemPage: number;
  onChangePage: (pageNumber: number) => void;
  onChangeCountItemPage: (pageNumber: number) => void;
  setSelectedTransfer: (data: TransferRequestResponse) => void;
}

export const TransferTable = (props: ITableProps) => {
  const { t } = useTranslation('payments');

  const columns = useMemo(
    () => [
      t('transferRequest.columns.status'),
      t('transferRequest.columns.date'),
      t('transferRequest.columns.transactionReference'),
      t('transferRequest.columns.transactionType'),
      t('transferRequest.columns.from'),
      t('transferRequest.columns.to'),
      t('transferRequest.columns.amount'),
      t('transferRequest.columns.fee'),
    ],
    [t],
  );
  return (
    <Table
      isScrollable
      isLoading={props.isLoading}
      error={props.error}
      onRefresh={props.onRefresh}
      columns={columns}
      items={props.data?.map((row) => {
        const color = ColorUtils.getTransferRequestStatusStyles(row.status);
        return [
          {
            type: TableCellType.Custom,
            value: (
              <div className={cn('whitespace-nowrap px-2 py-1 text-center', color.bg, color.text)}>
                {t(`statuses.transferRequest.${row.status}`, { ns: 'common' })}
              </div>
            ),
          },
          { type: TableCellType.Text, value: DateUtils.formatDate(row.createdOn || '') },
          { type: TableCellType.Text, value: row.reasonOfThePayment },
          { type: TableCellType.Text, value: FormatUtils.transactionTypeTransformation(t(`transferRequestType.${row.type}`, { ns: 'common' })) },
          { type: TableCellType.Text, value: row.fromAccount.name },
          { type: TableCellType.Text, value: row.beneficiaryAccount?.name || row?.recipient?.name || '-' },
          { type: TableCellType.Text, value: AmountUtils.transformToAmountWithCurrency(row.senderAmount, row.senderCurrency) },
          { type: TableCellType.Text, value: AmountUtils.transformToAmountWithCurrency(row.fee, row.senderCurrency) },
        ];
      })}
      pagination={{
        currentPage: props.currentPage,
        totalItemCount: props.totalItemCount,
        countItemPage: props.countItemPage,
        onChangePage: props.onChangePage,
        onChangeCountItemPage: props.onChangeCountItemPage,
      }}
      onClick={(rowIndex) => props.setSelectedTransfer(props.data[rowIndex])}
    />
  );
};
